// TODO: TRAILS-201 - migrate trail analytics to analytics library

import { TrackId } from '@alltrails/shared/types/track';
import { PageSource } from 'components/shared/Page/Page';
import { TrailId } from 'types/Trails';
import { IdKey } from '@alltrails/shared/types/lists';
import { getAmplitudeInstance } from '@alltrails/analytics/amplitudeHelpers';
import { AmplitudeEvent } from './constants/AmplitudeEvent';

export enum TrailDetailsReferralSources {
  Homepage = 'homepage river',
  ListView = 'list view',
  MapView = 'map view',
  Seo = 'seo',
  Deeplink = 'deeplink',
  Search = 'search'
}

export enum TrailDetailsActivityLocations {
  Review = 'review',
  ActivitiesTab = 'activities tab'
}

export enum TrailDetailsOverflowItem {
  SuggestTrailEdit = 'suggest trail edit',
  CustomizeMap = 'customize map'
}

export enum TrailDetailsTabItem {
  Description = 'description',
  Waypoints = 'waypoints',
  Contact = 'contact',
  Tips = 'tips',
  Weather = 'weather',
  UvIndex = 'uv index',
  Daylight = 'daylight',
  Reviews = 'reviews',
  Activities = 'activities',
  Photos = 'photos',
  Completed = 'completed',
  Facilities = 'facilities',
  GettingThere = 'getting there',
  Alerts = 'alerts',
  AdminNotes = 'admin notes'
}

export enum TrailDetailsPhotoLocations {
  HeroImage = 'hero image',
  PhotosTab = 'photos tab'
}

enum TrailDownloadLocations {
  TrailDetails = 'trail details',
  TrailCard = 'trail card'
}

export enum ListContentType {
  Favorites = 'favorites',
  Custom = 'custom',
  Completed = 'completed'
}

enum TrailDetailsUGCSortingMethods {
  AllTrails = 'alltrails sort',
  Newest = 'newest first',
  Oldest = 'oldest firest',
  Highest = 'highest rated',
  Lowest = 'lowest rated'
}

// maps the UGC sort types to it's respective enum value
type UgcSortMap = {
  atSort: TrailDetailsUGCSortingMethods.AllTrails;
  desc: TrailDetailsUGCSortingMethods.Newest;
  asc: TrailDetailsUGCSortingMethods.Oldest;
  highestRated: TrailDetailsUGCSortingMethods.Highest;
  lowestRated: TrailDetailsUGCSortingMethods.Lowest;
};
const UGC_SORT_MAP: UgcSortMap = Object.freeze({
  atSort: TrailDetailsUGCSortingMethods.AllTrails,
  desc: TrailDetailsUGCSortingMethods.Newest,
  asc: TrailDetailsUGCSortingMethods.Oldest,
  highestRated: TrailDetailsUGCSortingMethods.Highest,
  lowestRated: TrailDetailsUGCSortingMethods.Lowest
});

const getTrailReferralSourceFromPage = (page: PageSource): TrailDetailsReferralSources | null => {
  switch (page) {
    case PageSource.Home:
      return TrailDetailsReferralSources.Homepage;
    case PageSource.Explore:
      return TrailDetailsReferralSources.MapView;
    case PageSource.Seo:
      return TrailDetailsReferralSources.Seo;
    default:
      return null;
  }
};

const getTrailTabItemFromKey = (key: string): TrailDetailsTabItem => {
  switch (key) {
    case 'parking_facilities':
      return TrailDetailsTabItem.Facilities;
    case 'contact_info':
      return TrailDetailsTabItem.Contact;
    case 'getting_there':
      return TrailDetailsTabItem.GettingThere;
    case 'key_points':
      return TrailDetailsTabItem.AdminNotes;
    case 'uv_index':
      return TrailDetailsTabItem.UvIndex;
    case 'day_light':
      return TrailDetailsTabItem.Daylight;
    case 'Recordings':
      return TrailDetailsTabItem.Activities;
    default:
      return key?.toLowerCase() as TrailDetailsTabItem;
  }
};

const logTrailCardClicked = (trailId: TrailId, referralSource: TrailDetailsReferralSources): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailCardClicked, { 'trail id': trailId, location: referralSource });
};

const logTrailCardClickedFromPage = (trailId: TrailId, location: PageSource): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailCardClicked, { 'trail id': trailId, location: getTrailReferralSourceFromPage(location) });
};

const logTrailDetailsActivityViewed = (location: TrailDetailsActivityLocations, activityId: TrackId, reviewId: string, trailId: TrailId): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsActivityViewed, {
    'activity id': activityId,
    location,
    'review id': reviewId,
    'trail id': trailId
  });
};

const logTrailDetailsDirectionsClicked = (trailId: TrailId): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsDirectionsClicked, { 'trail id': trailId });
};

const logTrailDetailsNearbyTrailClicked = (trailId: TrailId, destinationTrailId: TrailId): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsNearbyTrailClicked, { 'trail id': trailId, 'destination trail id': destinationTrailId });
};

const logTrailDetailsOverflowClicked = (trailId: TrailId): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsOverflowClicked, { 'trail id': trailId });
};

const logTrailDetailsOverflowItemSelected = (trailId: number, item: TrailDetailsOverflowItem): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsOverflowItemSelected, { 'trail id': trailId, item });
};

const logTrailDetailsPrintClicked = (trailId: number): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsPrintClicked, { 'trail id': trailId });
};

const logTrailDetailsShareClicked = (trailId: number): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsShareClicked, { 'trail id': trailId });
};

const logTrailDetailsShowMoreClicked = (trailId: number): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsShowMoreClicked, { 'trail id': trailId });
};

const logTrailDownloadButtonTapped = (trailId: number): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDownloadButtonTapped, { 'trail id': trailId, location: TrailDownloadLocations.TrailDetails });
};

const logTrailSaveCompleted = (trailId: number, listId: IdKey, listType: ListContentType, isNewList: boolean, removedFromList: boolean): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailSaveCompleted, {
    'trail id': trailId,
    'list id': listId,
    'list type': listType,
    'is new list': isNewList,
    'removed from list': removedFromList
  });
};

const logTrailDetailsScrollDepth = (trailId: number, tabKey: string, allShown: boolean) => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsScrollDepth, {
    'trail id': trailId,
    'ugc tab': getTrailTabItemFromKey(tabKey),
    'all ugc shown': allShown
  });
};

const logTrailDetailsUgcSeeMoreClicked = (trailId: number, feedType: string) => {
  const trailIdProperty = { 'trail id': trailId };
  switch (feedType) {
    case 'reviews':
      getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsSeeAllReviewsClicked, trailIdProperty);
      break;
    case 'photos':
      getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsSeeAllPhotosClicked, trailIdProperty);
      break;
    case 'tracks':
      getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsSeeAllActivitiesClicked, trailIdProperty);
      break;
    default:
    // Do nothing
  }
};

const logTrailDetailsSortingMethodChanged = (trailId: TrailId, newSort: keyof UgcSortMap, oldSort: keyof UgcSortMap, tabKey: string) => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsSortingMethodChanged, {
    'trail id': trailId,
    new: UGC_SORT_MAP[newSort],
    old: UGC_SORT_MAP[oldSort],
    'ugc tab': getTrailTabItemFromKey(tabKey)
  });
};

const logTrailDetailsPhotoClicked = (trailId: TrailId, photoId: number, source: TrailDetailsPhotoLocations) => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsPhotoClicked, { 'trail id': trailId, 'photo id': photoId, source });
};

const logTrailDetailsTabClicked = (trailId: TrailId, tabKey: string): void => {
  getAmplitudeInstance().logEvent(AmplitudeEvent.TrailDetailsTabClicked, { 'trail id': trailId, tab: getTrailTabItemFromKey(tabKey) });
};

export {
  logTrailCardClicked,
  logTrailCardClickedFromPage,
  logTrailDetailsActivityViewed,
  logTrailDetailsDirectionsClicked,
  logTrailDetailsNearbyTrailClicked,
  logTrailDetailsOverflowClicked,
  logTrailDetailsOverflowItemSelected,
  logTrailDetailsPhotoClicked,
  logTrailDetailsPrintClicked,
  logTrailDetailsShareClicked,
  logTrailDetailsScrollDepth,
  logTrailDetailsShowMoreClicked,
  logTrailDetailsSortingMethodChanged,
  logTrailDetailsTabClicked,
  logTrailDetailsUgcSeeMoreClicked,
  logTrailDownloadButtonTapped,
  logTrailSaveCompleted
};
